// Static blog content. Two pilot articles in CS and EN. Adding another
// article: append an entry to `POSTS` — no other change needed, routing
// and sitemap pick it up automatically.

export interface BlogPost {
  slug: string;
  lang: "cs" | "en";
  title: string;
  description: string;
  author: string;
  date: string; // ISO YYYY-MM-DD
  readingMinutes: number;
  bodyHtml: string;
}

const CS_LEAGUE_SETUP = `
<p>Založit tipovací ligu na Ligu mistrů 2026/27 je otázka pěti minut — pokud víte, co má vzniknout, než rozešlete pozvánky. Následující článek shrnuje osvědčený postup pro partu 5–30 lidí v hospodské, firemní i rodinné tipovačce.</p>

<h2>Než se pustíte do zakládání</h2>
<p>Ujasněte si tři věci: <strong>o co se hraje</strong>, <strong>kdo bude admin</strong> a <strong>jaká pravidla bodování</strong>. První dvě jsou hlavně o lidech. Odměnu si nemusíte složitě vymýšlet — často stačí lahev vína nebo závazek, že vítěz nemusí platit další "rundu" ve skupinovém chatu.</p>

<h3>Kdo bude admin</h3>
<p>Admin je zodpovědný za nastavení pravidel, schvalování pozvánek a případné ruční opravy výsledků. Ideálně někdo, kdo turnaj bude určitě sledovat celý a nezmizí uprostřed na dvoutýdenní dovolenou bez signálu.</p>

<h3>Jaká pravidla bodování</h3>
<p>V <a href="/scoring-rules">samostatném článku o bodování</a> najdete detailní rozbor. Pro první ligu doporučujeme neexperimentovat a nechat výchozí nastavení <strong>3 / 1</strong>: 3 body za přesný výsledek, 1 bod za správnou tendenci (trefeného vítěze nebo remízu). Je to vyvážené a hráči to intuitivně chápou bez vysvětlování.</p>

<h2>Kroky založení ligy v Tipomaticu</h2>
<ol>
  <li><strong>Registrace.</strong> Otevřete <a href="/auth">tipomatic.app/auth</a> a zaregistrujte se e-mailem nebo přes Google.</li>
  <li><strong>Vytvoření ligy.</strong> V přehledu klikněte „Vytvořit ligu“, pojmenujte ji a vyberte Ligu mistrů 2026/27.</li>
  <li><strong>Kontrola pravidel.</strong> Otevřete nastavení ligy, zkontrolujte bodování a případně zapněte Speciální tipy.</li>
  <li><strong>Popis ligy.</strong> Do popisu napište, o co se hraje a jaká je uzávěrka registrace. Podporujeme jednoduché HTML — nadpisy, tučné, odkazy.</li>
  <li><strong>Sdílení pozvánky.</strong> Zkopírujte pozvánkový odkaz a rozešlete ho.</li>
</ol>

<h2>Jak napsat pozvánku, kterou lidi otevřou</h2>
<p>Nejčastější chyba: pozvánka bez kontextu. Když do skupinového chatu prostě přilepíte URL, polovina lidí ji ignoruje. Fungující šablona vypadá takto:</p>
<blockquote class="border-l-2 border-primary/50 pl-4 italic text-pitch-cream/70">
  <p>Ahoj, na Ligu mistrů rozjíždíme tipovačku „Hospoda U Vencla“. Bez peněz, jen o čest a lahev vína pro vítěze. Přidejte se do neděle, ať stihnete natipovat první kolo. Odkaz: <em>tipomatic.app/invite/…</em></p>
</blockquote>
<p>Tři zásadní věci: <strong>o co jde</strong> (tipovačka), <strong>o co se hraje</strong> (motivace i pro lidi, kteří netipují často) a <strong>dokdy se přidat</strong> (deadline = konverze).</p>

<h2>Nejčastější potíže a jak jim předejít</h2>

<h3>"Tohle se mi nezobrazuje na mobilu"</h3>
<p>Tipomatic si můžete nainstalovat jako aplikaci (návod máme v <a href="/help">nápovědě</a>). Pak se otevírá jedním klepnutím přímo z plochy telefonu.</p>

<h3>"Zapomněl jsem natipovat zápas"</h3>
<p>Před turnajem využijte <strong>hromadné tipování</strong> — více zápasů natipujete pohodlně na jednom místě. Jednotlivé tipy můžete až do uzamčení upravovat.</p>

<h3>"Někdo v lize podvádí a šeptá tipy ostatním"</h3>
<p>Tipy ostatních hráčů se odemknou <strong>až po výkopu zápasu</strong>. Předtím vidíte jen svoje. Podvádět by tedy šlo jen mimo aplikaci — a proti tomu žádná technická obrana neexistuje. Ale klid, na 30 lidech to nikoho nebaví dělat.</p>

<h2>Po skončení turnaje</h2>
<p>Po posledním zápase žebříček uzamkne a vítěz je jasný. Doporučujeme udělat screenshot finální tabulky a hodit ho do skupinového chatu s krátkým shrnutím. Do další ligy si můžete většinu nastavení zkopírovat — jen změníte turnaj a rozešlete nový pozvánkový odkaz.</p>

<p>Přejeme dobré tipy. Držte se svých oblíbenců, ale občas dejte šanci i outsiderovi — právě překvapení často rozhodují pořadí celé ligy.</p>
`;

const CS_MISTAKES = `
<p>Ligová fáze Ligy mistrů nabízí spoustu zápasů, rozdílné herní styly a překvapení. Zároveň je snadné ztratit náskok několika uspěchanými tipy. Tady je pět častých chyb — a jak se jim vyhnout.</p>

<h2>Chyba 1: Tipovat příliš málo gólů</h2>
<p>Tip <strong>1:0</strong> zní bezpečně — favorit vyhraje, ale ne o moc. Pokud však čekáte otevřený zápas, může být rozumnější uvažovat i o 2:1 nebo 2:0. Přesné skóre obvykle přináší více bodů než pouhý správný výsledek.</p>
<p><strong>Řešení:</strong> zohledněte herní styl obou klubů, domácí prostředí i aktuální formu. Jeden univerzální výsledek nefunguje pro každý zápas.</p>

<h2>Chyba 2: Nevěřit outsiderům</h2>
<p>I velké evropské kluby ztrácejí body proti papírově slabším soupeřům. Tipovat pokaždé jen slavnější jméno proto nemusí být nejlepší cesta.</p>
<p><strong>Řešení:</strong> v každém kole si vytipujte zápas, ve kterém může rozhodnout forma, únava nebo domácí prostředí. Překvapení může v těsném žebříčku udělat velký rozdíl.</p>

<h2>Chyba 3: Tipovat všechny zápasy dopředu a už se k nim nevrátit</h2>
<p>Bulk tipování je pohodlné, ale <strong>nikdo nedokáže odhadnout formu za 3 týdny dopředu</strong>. Klíčoví hráči se zraní, tréninkové kempy se zvrhnou, počasí zamíchá kartami.</p>
<p><strong>Řešení:</strong> po každém kole si vyhraďte 10 minut a projděte tipy na následující den. Formu týmů znáte teď víc než při zakládání ligy. Tipomatic vám dovolí upravovat tipy až do vteřiny před výkopem.</p>

<h2>Chyba 4: Podléhat srdci u vlastního týmu</h2>
<p>Fanoušek svého klubu snadno přecení jeho šance i proti silnému soupeři. Emocionální tipy pak mohou rozhodnout o několika místech v tabulce.</p>
<p><strong>Řešení:</strong> držte se pravidla „tipuj střízlivě, fandit můžeš u zápasu“. Radost z nečekané výhry vám zůstane i bez bodů za tip.</p>

<h2>Chyba 5: Nekontrolovat žebříček a pravidla ligy</h2>
<p>Někteří hráči za celý turnaj neotevřou žebříček ani jednou. Poté ke konci turnaje překvapeně zjistí, že jsou třetí od konce, protože nevěděli, že admin nastavil bonus za playoff.</p>
<p><strong>Řešení:</strong> po každém kole se podívejte na žebříček a na kartu ligy — vidíte tam bodovací pravidla i to, kolik bodů získali hráči nad vámi za jaké zápasy. Když někdo výrazně vyskočil, poznáte, jestli to bylo štěstím na jednom přesném tipu nebo systematickou strategií. Můžete se přizpůsobit.</p>

<h2>Bonus: Skupinová psychologie</h2>
<p>Pokud vidíte, že v lize jsou tři hráči těsně vedoucí a vy jste čtvrtý s minimální ztrátou, <strong>tipujte opačně než oni</strong> u zbývajících zápasů. Pokud trefí, ztratíte málo; pokud ne, přeskočíte je. Když je naopak ztráta velká, hrajte "safe" jako oni — jakákoli změna pořadí je pak náhoda ve váš prospěch.</p>

<p>Hodně štěstí u vaší ligy. Doufáme, že vám tento článek ušetří pár zbytečných ztrát bodů.</p>
`;

const EN_LEAGUE_SETUP = `
<p>Setting up a Champions League 2026/27 prediction league takes about five minutes — provided you know what you want before sending invitations. This article covers a practical setup for pub, office, and family leagues.</p>

<h2>Before you start</h2>
<p>Nail down three things: <strong>what you're playing for</strong>, <strong>who runs the league</strong>, and <strong>what the scoring rules are</strong>. The first two are people problems. The prize doesn't need to be fancy — a bottle of wine or "loser buys the next round" usually works.</p>

<h3>Who's the admin</h3>
<p>The admin sets the rules, approves invites, and occasionally corrects a result manually. Pick someone who will definitely follow the whole tournament and won't disappear on a two-week holiday without signal in the middle.</p>

<h3>What scoring rules</h3>
<p>There's a <a href="/scoring-rules">detailed article on scoring</a>. For a first league, we recommend not experimenting and keeping the defaults <strong>3 / 1</strong>: 3 points for the exact score, 1 point for the correct outcome (right winner or a draw). It's balanced and players intuitively get it without explanation.</p>

<h2>Setup steps in Tipomatic</h2>
<ol>
  <li><strong>Register.</strong> Go to <a href="/auth">tipomatic.app/auth</a> and sign up with email or Google.</li>
  <li><strong>Create the league.</strong> From the overview, hit “Create league”, name it, and pick Champions League 2026/27.</li>
  <li><strong>Check the rules.</strong> Open league settings, confirm scoring, and optionally enable Special Tips.</li>
  <li><strong>Write the description.</strong> Explain what you're playing for and when the sign-up closes. Simple HTML supported.</li>
  <li><strong>Share the invite.</strong> Copy the invite link and send it out.</li>
</ol>

<h2>How to write an invite people will actually open</h2>
<p>Most common mistake: no context. If you drop a bare URL into the group chat, half the people will ignore it. This template works:</p>
<blockquote class="border-l-2 border-primary/50 pl-4 italic text-pitch-cream/70">
  <p>Hey — we're running a Champions League prediction game called “The Regulars”. No money, just bragging rights and a bottle for the winner. Join by Sunday so you can predict the opening round. Link: <em>tipomatic.app/invite/…</em></p>
</blockquote>
<p>Three essentials: <strong>what it is</strong>, <strong>what you're playing for</strong>, and <strong>the deadline</strong>. A deadline is what converts casual readers into signups.</p>

<h2>Common problems and how to avoid them</h2>

<h3>"This doesn't work on my phone"</h3>
<p>You can install Tipomatic as an app (guide in the <a href="/help">help section</a>) and open it from your phone's home screen with one tap.</p>

<h3>"I forgot to predict a match"</h3>
<p>Use <strong>bulk predictions</strong> before the tournament to cover multiple fixtures in one place. You can still adjust individual matches until they lock.</p>

<h3>"Someone is cheating by whispering picks"</h3>
<p>Other players' predictions unlock <strong>only after kickoff</strong>. Before that you see only your own. Cheating would have to happen outside the app — no technical defense against that. In practice, in a 30-person league nobody bothers.</p>

<h2>After the tournament</h2>
<p>After the last match the leaderboard locks and the winner is clear. Take a screenshot of the final table and drop it in the group chat with a short summary. For the next league you can reuse most of the settings — just change the tournament and send a new invite link.</p>

<p>Good luck with your league. Stick with your favourites, but give an underdog a chance now and then — surprises often decide the final standings.</p>
`;

const EN_MISTAKES = `
<p>The Champions League league phase brings plenty of fixtures, contrasting styles, and surprises. It is also easy to lose an early lead with a few rushed picks. Here are five common mistakes — and how to avoid them.</p>

<h2>Mistake 1: Predicting too few goals</h2>
<p>A <strong>1:0</strong> prediction sounds safe — the favourite wins, but not by much. In an open match, however, 2:1 or 2:0 may be more realistic. An exact score usually earns more than merely picking the right outcome.</p>
<p><strong>Fix:</strong> consider both clubs' playing style, home advantage, and current form. One universal score does not fit every fixture.</p>

<h2>Mistake 2: Not trusting the underdogs</h2>
<p>Even Europe's biggest clubs drop points against less-fancied opponents. Picking the more famous name every time is not always the best approach.</p>
<p><strong>Fix:</strong> identify one fixture per round where form, fatigue, or home advantage could create an upset. One bold pick can make a big difference in a tight table.</p>

<h2>Mistake 3: Predicting everything up front and never coming back</h2>
<p>Bulk predictions are convenient but <strong>nobody predicts form 3 weeks ahead</strong>. Key players get injured, training camps go sideways, weather shifts things.</p>
<p><strong>Fix:</strong> after every round, spend 10 minutes reviewing the next day's picks. You know form better now than at league creation. Tipomatic lets you edit predictions right up to kickoff.</p>

<h2>Mistake 4: Letting your heart pick for your own team</h2>
<p>Supporters can easily overestimate their own club, even against a stronger opponent. Emotional picks can cost several places in the table.</p>
<p><strong>Fix:</strong> predict with your head and cheer with your heart. An unexpected win will still feel great even without prediction points.</p>

<h2>Mistake 5: Ignoring the leaderboard and the rules</h2>
<p>Some players never check the leaderboard during the tournament. Then they're surprised near the end that they're 3rd from the bottom because the admin set a playoff bonus they didn't know about.</p>
<p><strong>Fix:</strong> check the leaderboard after each round. You'll see how much people above you scored for which matches. When someone jumps up, you can tell if it was one lucky exact score or a systematic strategy — and adjust.</p>

<h2>Bonus: League psychology</h2>
<p>If three players are tightly ahead and you're 4th with a small gap, <strong>predict against them</strong> for the remaining matches. If they hit, you lose little; if they miss, you jump past them. When the gap is large, play "safe" like them — any reshuffle is then chance in your favour.</p>

<p>Good luck with your league. Hopefully this saves you a few unnecessary points.</p>
`;

export const POSTS: BlogPost[] = [
  {
    slug: "jak-zalozit-tipovaci-ligu-ms-2026",
    lang: "cs",
    title: "Jak založit tipovací ligu na Ligu mistrů 2026/27",
    description: "Praktický postup krok za krokem: nastavení pravidel a Speciálních tipů, výběr admina a pozvánka pro spoluhráče.",
    author: "Lukáš Skywalker",
    date: "2026-06-15",
    readingMinutes: 6,
    bodyHtml: CS_LEAGUE_SETUP,
  },
  {
    slug: "nejcastejsi-chyby-tipovani-skupinove-faze",
    lang: "cs",
    title: "5 častých chyb při tipování Ligy mistrů",
    description: "Jak se vyhnout stereotypním výsledkům, nepřeceňovat favority a pracovat s průběžným pořadím v soukromé lize.",
    author: "Lukáš Skywalker",
    date: "2026-06-22",
    readingMinutes: 5,
    bodyHtml: CS_MISTAKES,
  },
  {
    slug: "how-to-set-up-world-cup-prediction-league-2026",
    lang: "en",
    title: "How to set up a Champions League 2026/27 prediction league",
    description: "A practical setup covering league rules, Special Tips, choosing an admin, and writing an invite people actually open.",
    author: "Lukáš Skywalker",
    date: "2026-06-15",
    readingMinutes: 6,
    bodyHtml: EN_LEAGUE_SETUP,
  },
  {
    slug: "top-5-group-stage-prediction-mistakes",
    lang: "en",
    title: "Top 5 Champions League prediction mistakes",
    description: "Avoid repetitive scores, overrating favourites, emotional picks, and other mistakes that cost places in your private league.",
    author: "Lukáš Skywalker",
    date: "2026-06-22",
    readingMinutes: 5,
    bodyHtml: EN_MISTAKES,
  },
];

export function getPost(slug: string): BlogPost | undefined {
  return POSTS.find((p) => p.slug === slug);
}
